import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import './Projects.css';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = [
    {
      title: 'Metallic Surface Defect Detection Dashboard',
      description:
        'Built full-stack AI defect detection system with FastAPI backend implementing PaDiM anomaly localization, KNN distance metrics, and an interactive React/Vite dashboard with real-time heatmap and explainable AI visualizations. Developed PyTorch ResNet50 computer vision pipeline with multi-scale feature extraction, per-class memory banks, and anomaly scoring achieving 100% accuracy across 6 defect classes.',
      tags: ['React', 'PyTorch', 'OpenCV', 'ResNet50'],
      github: 'https://github.com/manishtandurkar/defect-detection',
      demo: null,
    },
    {
      title: 'Lost & Found - Campus Recovery Hub',
      description:
        'Built full-stack Android app with MVVM architecture, Firebase Realtime Database backend, and offline-first Room caching with auto-sync on reconnect. Developed background notification system using FCM push delivery and a matching service that auto-alerts users on lost-found keyword matches. Created Google Maps module with custom Canvas-drawn markers, info windows, and pin-drop location picker with reverse geocoding for item location tracking.',
      tags: ['Java', 'Firebase', 'Room', 'Google Maps SDK'],
      github: 'https://github.com/manishtandurkar/Lost-and-Found',
      demo: null,
    },
    {
      title: 'Secure Chat - E2EE Multi-Client Messaging System',
      description:
        'Built a production-grade end-to-end encrypted chat system in C combining a concurrent TCP/TLS 1.3 server with the Signal Double Ratchet protocol for per-message forward secrecy and break-in recovery. Implemented a dual-transport layer (TCP + UDP) with a dedup ring buffer, an adaptive security engine that escalates retries, padding, and key rotation based on measured packet loss and auth failures, plus a per-IP intrusion detection system and persistent offline message queue with re-encryption on reconnect.',
      tags: ['C', 'OpenSSL', 'TLS 1.3', 'pthreads', 'GTK+3'],
      github: 'https://github.com/manishtandurkar/secure-chat',
      demo: null,
    },
    {
      title: 'Abhyasa - AI-Driven Smart Notes Platform',
      description:
        'Developed full-stack AI-powered platform digitizing educational notes using Google Cloud Vision API OCR with 95%+ accuracy and automated quiz generation via Gemini Pro API. Built Flask REST API backend with Firebase Authentication, AWS S3 file storage, and Firestore database, handling 50+ concurrent users with containerized Docker deployment on AWS App Runner. Designed responsive React frontend with real-time analytics dashboard, flashcard system, and PDF viewer, improving study preparation time by 60% through intelligent content analysis.',
      tags: ['React', 'Flask', 'Firebase', 'AWS', 'Google Cloud'],
      github: 'https://github.com/Monishaa19/Abhyasa',
      demo: null,
    },
    {
      title: 'LLVM Function-Level Profiling Instrumentor',
      description:
        'Built a custom LLVM ModulePass in C++ that traverses program IR using LoopInfo analysis to detect loop headers, and injects global profiling counters via IRBuilder at function entry points and loop headers to measure call frequency and iteration counts. Developed an end-to-end CLI pipeline (Bash) and full-stack web interface (FastAPI backend, React/Vite frontend with Monaco editor and Recharts visualizations) for in-browser C code profiling, validated against recursion, sorting, and nested-loop benchmark programs.',
      tags: ['LLVM', 'C++', 'FastAPI', 'React'],
      github: 'https://github.com/manishhp/llvm-function-profiler',
      demo: null,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="projects" className="py-28 glow-projects">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container mx-auto px-6 max-w-6xl"
      >
        <motion.h2
          variants={itemVariants}
          className="text-5xl md:text-6xl font-bold font-poppins tracking-tight text-white mb-4"
        >
          My <span className="gradient-text">Projects</span>
        </motion.h2>
        <motion.div
          variants={itemVariants}
          className="w-24 h-px bg-gradient-to-r from-primary-400 to-transparent mb-16"
        />

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -6 }}
              className={`group relative glass rounded-3xl overflow-hidden shadow-card hover:border-primary-400/40 hover:shadow-neon transition-all duration-300 flex flex-col ${
                index === 0 ? 'md:col-span-2' : ''
              }`}
            >
              {/* Top hairline */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent" />

              {/* Project Content */}
              <div className="p-8 flex-1 flex flex-col relative">
                <h3 className="text-2xl font-bold font-poppins tracking-tight mb-3 text-white">
                  {project.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-primary-500/10 text-primary-300 border border-primary-400/20 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Links */}
                <div className="flex space-x-6">
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-secondary-300 transition-colors duration-300 flex items-center text-sm font-medium"
                    >
                      <FaGithub className="mr-2" />
                      GitHub
                    </a>
                  )}
                  {project.demo && (
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-secondary-300 transition-colors duration-300 flex items-center text-sm font-medium"
                    >
                      <FaExternalLinkAlt className="mr-2" />
                      Demo
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Projects;
